import { EventEmitter, Injectable } from '@angular/core'
import { AuthenticationService } from './authentication.service'
import { BehaviorSubject, of } from 'rxjs'
import { UserProfile } from '../models/UserProfile'
import { Apollo } from 'apollo-angular'
import { gql } from '@apollo/client/core'
import { Address } from '../../../modules/deals/configurator/ui/models/address'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { catchError } from 'rxjs/operators'

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class UserProfileService {
    userProfile: BehaviorSubject<UserProfile | null> = new BehaviorSubject(null)

    userProfilePersisted: EventEmitter<UserProfile> = new EventEmitter<UserProfile>()

    constructor(private apollo: Apollo, authenticationService: AuthenticationService) {
        // always recieve the latest and greatest profileId from logedIn user
        // (or null if user is not logged in)
        authenticationService.currentUser.pipe(untilDestroyed(this)).subscribe(this.loadUserProfile.bind(this))
    }

    async persistUserProfile(userProfile?: UserProfile): Promise<void> {
        // persist locally
        if (!userProfile) {
            userProfile = this.userProfile.getValue()
        } else {
            const currentUserProfile = this.userProfile.getValue()

            if (currentUserProfile) {
                userProfile = {
                    ...currentUserProfile,
                    ...userProfile,
                }
            }

            this.userProfile.next(userProfile)
        }

        const {
            firstName,
            legalFirstNames,
            lastName,
            lastNamePrefix,
            telephoneNumber,
            zipcode,
            houseNumber,
            houseNumberSuffix,
            street,
            city,
        } = {
            ...(userProfile.address || new Address()),
            ...userProfile,
        }

        const variables = {
            firstName: firstName.trim(),
            legalFirstNames: legalFirstNames.trim(),
            lastName: lastName.trim(),
            lastNamePrefix: lastNamePrefix.trim(),
            telephoneNumber,
            zipcode,
            houseNumber: parseInt(houseNumber),
            houseNumberSuffix,
            street,
            city,
        }

        // perist on server
        await this.apollo
            .mutate({
                mutation: gql`
                    mutation persistUserProfile(
                        $firstName: String
                        $legalFirstNames: String
                        $lastName: String
                        $lastNamePrefix: String
                        $telephoneNumber: String
                        $zipcode: String
                        $houseNumber: Int
                        $houseNumberSuffix: String
                        $street: String
                        $city: String
                    ) {
                        updateMyUserProfile(
                            firstName: $firstName
                            legalFirstNames: $legalFirstNames
                            lastName: $lastName
                            lastNamePrefix: $lastNamePrefix
                            telephoneNumber: $telephoneNumber
                            address: {
                                zipcode: $zipcode
                                houseNumber: $houseNumber
                                houseNumberSuffix: $houseNumberSuffix
                                street: $street
                                city: $city
                            }
                        ) {
                            id
                        }
                    }
                `,
                variables,
            })
            .toPromise()

        this.userProfilePersisted.emit(userProfile)
    }

    async loadUserProfile(user): Promise<void> {
        if (user === null) {
            this.userProfile.next(null)
        } else {
            this.userProfile.next(await this.getUserProfile())
        }
    }

    async getUserProfile(): Promise<UserProfile> {
        const {
            data: { myUserProfile: userProfile },
        } = await this.apollo
            .query<any>({
                query: gql`
                    {
                        myUserProfile {
                            id
                            userId
                            emailAddress
                            firstName
                            legalFirstNames
                            lastName
                            lastNamePrefix
                            telephoneNumber
                            referralCode
                            address {
                                id
                                street
                                houseNumber
                                houseNumberSuffix
                                zipcode
                                city
                            }
                        }
                    }
                `,
            })
            .pipe(
                catchError(err => {
                    if (err.message === 'No profile found') {
                        return of({ data: { myUserProfile: null } })
                    }
                })
            )
            .toPromise()

        return userProfile
    }
}
